import { memo } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ExternalLink } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

/**
 * FAQ Section - HOMEPAGE VERSION
 * 
 * RULE: NO informational keywords on homepage!
 * - "vad är thca", "är thca lagligt", "thca vs thc" belong ONLY on blogs
 * - Homepage FAQ focuses on COMMERCIAL questions (buying, shipping, payment)
 * 
 * Facts include external source links. Experience is clearly labeled.
 */
const faqs = [
  // === COMMERCIAL/TRANSACTIONAL QUESTIONS (belong on homepage) ===
  {
    question: "Hur snabbt levererar ni?",
    answer: "Beställningar före kl 14:00 skickas samma dag. Normal leveranstid är 1-3 vardagar med PostNord. Paketen skickas diskret utan synlig produktinformation. Du får spårningsnummer via e-post."
  },
  {
    question: "Är det fri frakt?",
    answer: "Ja, fri frakt på alla beställningar över 499 kr till hela Sverige."
  },
  {
    question: "Vilken styrka ska jag välja?",
    answer: "Magic Sauce har 99% renhet och passar de flesta. Om du är ny kan Snozy (80% renhet) passa bättre för en mildare upplevelse. Buds har 28-50% och hash har cirka 50%."
  },
  {
    question: "Hur länge håller en vape?",
    answer: "En Magic Sauce vape med 2ml kapacitet ger cirka 1000 puffar. Vid normal användning håller den 2-4 veckor. Förvara vapen svalt och stående för bästa hållbarhet."
  },
  {
    question: "Vilka betalningsmetoder accepterar ni?",
    answer: "Vi accepterar Visa, Mastercard och Revolut. Alla betalningar sker via krypterade anslutningar för din säkerhet."
  },
  {
    question: "Hur mycket kostar Magic Sauce vapes?",
    answer: "Våra vapes börjar från 749 kr för 2ml med 99% renhet. Vi erbjuder även paketpriser där du kan spara upp till 25%."
  },
  {
    question: "Kan jag spåra min beställning?",
    answer: "Ja, du får ett spårningsnummer via e-post när paketet skickas. Följ leveransen direkt på PostNords hemsida."
  },
  {
    question: "Kan jag returnera produkter?",
    answer: "Vi erbjuder 14 dagars ångerrätt på oöppnade produkter enligt distansavtalslagen. Öppnade produkter kan inte returneras av hygieniska skäl."
  },
  {
    question: "Är era produkter labbtestade?",
    answer: "Ja, alla produkter testas av oberoende laboratorier. Vi kontrollerar renhet, potens och föroreningar. Analysresultat kan begäras via kundtjänst.",
    hasSourceLink: true
  },
  {
    question: "Vad innehåller Magic Sauce Vape?",
    answer: "Magic Sauce Vape innehåller 99% ren THCA och naturliga terpener. Varje vape har 2ml kapacitet. Vi använder inga tillsatser, VG/PG eller smakämnen."
  },
  {
    question: "Hur förvarar jag mina produkter?",
    answer: "Förvara produkterna svalt (15-21°C) och mörkt. Vapes bör stå upprätt. Hash och buds förvaras i lufttäta behållare. Korrekt förvaring bevarar kvaliteten i månader."
  },
  {
    question: "Tips för första gången?",
    answer: "Börja med en låg dos – 1-2 puffar för vapes eller 0.1g för hash/buds. Vänta 5-10 minuter innan du tar mer. Välj en bekväm miljö och ha vatten tillgängligt."
  },
];

export const FAQ = memo(() => {
  return (
    <section id="faq" className="py-16 md:py-20 relative overflow-hidden bg-card/20">
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/15 border border-primary/40 mb-5">
            <HelpCircle className="w-4 h-4 text-primary" />
            <span className="text-sm font-bold text-primary">FAQ</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            Vanliga Frågor
          </h2>
          <p className="text-base text-foreground/70 max-w-xl mx-auto">
            Svar på de vanligaste frågorna om beställning och leverans
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index}
                value={`item-${index}`}
                className="bg-card/80 border border-border/50 rounded-xl px-5 data-[state=open]:border-primary/30 transition-all"
              >
                <AccordionTrigger className="text-left font-bold text-base hover:no-underline py-5 text-foreground hover:text-primary transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          {/* Link to informational content on blogs */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Vill du veta mer om THCA och laglighet?
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link 
                to="/blogg/vad-ar-thca" 
                className="text-sm text-primary hover:underline font-medium"
              >
                Läs: Vad är THCA? →
              </Link>
              <Link 
                to="/faq" 
                className="text-sm text-primary hover:underline font-medium"
              >
                Alla frågor →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

FAQ.displayName = "FAQ";
export default FAQ;